import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Users, Lightbulb, ArrowRight } from 'lucide-react';
import Accordion, { AccordionItem } from '../ui/Accordion';
import Card from '../ui/Card';
import { formatDuration } from '../../utils/formatters';

export default function SectionEtapes({ data }) {
  const { title, description, conceptExplanation, items } = data;

  return (
    <section id="etapes" className="section-padding bg-secondary-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">{title}</h2>
          {description && (
            <p className="text-xl text-secondary-200 max-w-3xl mx-auto">
              {description}
            </p>
          )}
        </motion.div>

        {/* Concept Explanation */}
        {conceptExplanation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto mb-12"
          >
            <Card className="bg-amber-900/20 border-2 border-amber-500/50">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-amber-500/20 text-amber-400 rounded-lg">
                  <Lightbulb size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3">
                    {conceptExplanation.title}
                  </h3>
                  <p className="text-amber-100 leading-relaxed mb-4">
                    {conceptExplanation.intro}
                  </p>
                  {conceptExplanation.details && (
                    <div className="space-y-2">
                      {conceptExplanation.details.map((detail, index) => (
                        <p key={index} className="text-amber-100/90 text-sm leading-relaxed">
                          {detail}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Example Section */}
              {conceptExplanation.example && (
                <div className="mt-6 pt-6 border-t border-amber-500/30">
                  <h4 className="text-lg font-bold text-white mb-4">
                    {conceptExplanation.example.title}
                  </h4>

                  <div className="bg-secondary-900/50 rounded-lg p-6 mb-4">
                    <p className="text-white font-semibold mb-2">
                      {conceptExplanation.example.conceptBase}
                    </p>
                    <p className="text-amber-100/80 text-sm">
                      {conceptExplanation.example.conceptDescription}
                    </p>
                  </div>

                  {/* Variations Included */}
                  <div className="mb-6">
                    <p className="text-amber-100 font-semibold mb-3">
                      {conceptExplanation.example.variationsIncluded.label}
                    </p>
                    <ul className="space-y-2 mb-4">
                      {conceptExplanation.example.variationsIncluded.items.map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-amber-100/90">
                          <CheckCircle2 size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-green-400 font-semibold flex items-center gap-2">
                      <ArrowRight size={18} />
                      {conceptExplanation.example.variationsIncluded.conclusion}
                    </p>
                  </div>

                  {/* New Concept Required */}
                  <div className="bg-red-900/20 rounded-lg p-6 border-l-4 border-red-500">
                    <p className="text-amber-100 font-semibold mb-3">
                      {conceptExplanation.example.newConceptRequired.label}
                    </p>
                    <ul className="space-y-2 mb-4">
                      {conceptExplanation.example.newConceptRequired.items.map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-amber-100/90">
                          <span className="text-red-400 flex-shrink-0">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-red-400 font-semibold flex items-center gap-2">
                      <ArrowRight size={18} />
                      {conceptExplanation.example.newConceptRequired.conclusion}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Timeline Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto"
        >
          <Accordion>
            {items.map((item) => (
              <AccordionItem
                key={item.id}
                title={
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="flex items-center justify-center w-8 h-8 bg-primary-500 text-white rounded-full text-sm font-bold">
                      {item.id}
                    </span>
                    <span className="flex-1 text-white">{item.title}</span>
                    <span className="text-sm text-secondary-200 flex items-center gap-1">
                      <Clock size={14} />
                      {item.duration}
                    </span>
                  </div>
                }
                defaultOpen={item.id === 1}
              >
                {/* Description */}
                <p className="text-secondary-200 mb-4 leading-relaxed">
                  {item.description}
                </p>

                {/* Détails */}
                {item.details && item.details.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 text-white">Détails :</h4>
                    <ul className="space-y-1.5">
                      {item.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-secondary-200">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Livrable */}
                {item.deliverable && (
                  <div className="bg-secondary-900 border-l-4 border-primary-500 p-4 mb-4">
                    <p className="text-sm">
                      <span className="font-semibold text-white">📦 Livrable : </span>
                      <span className="text-secondary-200">{item.deliverable}</span>
                    </p>
                  </div>
                )}

                {/* Prérequis */}
                {item.prerequisite && (
                  <div className="bg-amber-900/20 border-l-4 border-amber-500 p-4 mb-4">
                    <p className="text-sm">
                      <span className="font-semibold text-amber-400">⚠️ Prérequis : </span>
                      <span className="text-amber-200">{item.prerequisite}</span>
                    </p>
                  </div>
                )}

                {/* Validation */}
                {item.validation && (
                  <div className="bg-green-900/20 border-l-4 border-green-500 p-4">
                    <p className="text-sm">
                      <span className="font-semibold text-green-400">✓ Validation : </span>
                      <span className="text-green-200">{item.validation}</span>
                    </p>
                  </div>
                )}
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
